import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { getWebApiErrorMessage } from "@faire/web--source/common/getErrorMessage";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { logError } from "@faire/web--source/common/logging";
import { NumberParser } from "@faire/web--source/common/NumberParser";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { useMutation as usePostAdditionalInfoMutation } from "@faire/web-api--source/api/brand-lead/additional-info/post-hooks";
import { IBrandLeadApplicationAdditionalInfo } from "@faire/web-api--source/indigofair/data/IBrandLeadApplicationAdditionalInfo";
import { ICreateBrandLeadApplicationAdditionalInfoRequest } from "@faire/web-api--source/indigofair/data/ICreateBrandLeadApplicationAdditionalInfoRequest";
import { useState, useCallback } from "react";
export var useMakerSignUpAdditionalQuestions = function(brandLeadToken) {
    var _useState = _sliced_to_array(useState(), 2), yearEstablished = _useState[0], setYearEstablished = _useState[1];
    var _useState1 = _sliced_to_array(useState(), 2), instagramHandle = _useState1[0], setInstagramHandle = _useState1[1];
    var _useState2 = _sliced_to_array(useState(new Set()), 2), faireValues = _useState2[0], setFaireValues = _useState2[1];
    var _useState3 = _sliced_to_array(useState(), 2), additionalNotes = _useState3[0], setAdditionalNotes = _useState3[1];
    var _useState4 = _sliced_to_array(useState(false), 2), formSubmitted = _useState4[0], setFormSubmitted = _useState4[1];
    var _useState5 = _sliced_to_array(useState({}), 2), errors = _useState5[0], setErrors = _useState5[1];
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _usePostAdditionalInfoMutation = usePostAdditionalInfoMutation({
        onSuccess: function() {
            trackEvent("maker-application/tell-us-more/submit");
            setFormSubmitted(true);
        },
        onError: function(error) {
            var fallbackMessage = strictLocalize({
                id: "S13f2s",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Something went wrong. Please try again or visit our Help Center if you have any questions."
                    }
                ]
            });
            setErrors(function(prev) {
                return _object_spread_props(_object_spread({}, prev), {
                    submission: getWebApiErrorMessage(error, strictLocalize, fallbackMessage)
                });
            });
            logError(error);
        }
    }), postAdditionalInfo = _usePostAdditionalInfoMutation.mutate, isPostingAdditionalInfo = _usePostAdditionalInfoMutation.isPending;
    var handleYearEstablishedChanged = useCallback(function(e) {
        var numberParser = new NumberParser();
        var value = numberParser.parse(e.target.value);
        if (Number.isNaN(value) && e.target.value) {
            setErrors(function(prev) {
                return _object_spread_props(_object_spread({}, prev), {
                    yearEstablished: strictLocalize({
                        id: "oJBM0e",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Please enter a number"
                            }
                        ]
                    })
                });
            });
            setYearEstablished(undefined);
        } else {
            setYearEstablished(value);
            setErrors(function(prev) {
                var yearEstablished = prev.yearEstablished, rest = _object_without_properties(prev, [
                    "yearEstablished"
                ]);
                return rest;
            });
        }
    }, [
        strictLocalize
    ]);
    var handleInstagramHandleChanged = useCallback(function(e) {
        // Using this regex for validating Instagram handles: https://regexr.com/3cg7r
        var regex = /^(?!.*\.\.)(?!.*\.$)[^\W][\w.]{0,29}$/;
        if (!e.target.value.match(regex)) {
            setErrors(function(prev) {
                return _object_spread_props(_object_spread({}, prev), {
                    instagramHandle: strictLocalize({
                        id: "Dh0Rql",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Please enter a valid instagram handle"
                            }
                        ]
                    })
                });
            });
            setInstagramHandle(undefined);
        } else {
            setInstagramHandle(e.target.value);
            setErrors(function(prev) {
                var instagramHandle = prev.instagramHandle, rest = _object_without_properties(prev, [
                    "instagramHandle"
                ]);
                return rest;
            });
        }
    }, [
        strictLocalize
    ]);
    var isFaireValueSelected = useCallback(function(option) {
        return faireValues.has(option.value);
    }, [
        faireValues
    ]);
    var onChangeFaireValue = useCallback(function(option) {
        if (isFaireValueSelected(option)) {
            setFaireValues(function(prev) {
                var updatedSet = new Set(prev);
                updatedSet.delete(option.value);
                return updatedSet;
            });
        } else {
            setFaireValues(function(prev) {
                return new Set(_to_consumable_array(prev).concat([
                    option.value
                ]));
            });
        }
    }, [
        isFaireValueSelected
    ]);
    var onSave = useCallback(/*#__PURE__*/ _async_to_generator(function() {
        var submission, formErrors, request;
        return _ts_generator(this, function(_state) {
            submission = errors.submission, formErrors = _object_without_properties(errors, [
                "submission"
            ]);
            if (Object.keys(formErrors).length > 0) {
                setErrors(function(prev) {
                    return _object_spread_props(_object_spread({}, prev), {
                        submission: strictLocalize({
                            id: "PUfD0W",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Please address all values in red."
                                }
                            ]
                        })
                    });
                });
                return [
                    2
                ];
            } else {
                setErrors({});
            }
            request = ICreateBrandLeadApplicationAdditionalInfoRequest.build({
                additional_info: IBrandLeadApplicationAdditionalInfo.build({
                    brand_lead_token: brandLeadToken,
                    year_established: yearEstablished,
                    instagram_handle: instagramHandle,
                    faire_values: Array.from(faireValues),
                    additional_notes: additionalNotes
                })
            });
            postAdditionalInfo([
                request
            ]);
            return [
                2
            ];
        });
    }), [
        errors,
        strictLocalize,
        postAdditionalInfo,
        brandLeadToken,
        yearEstablished,
        instagramHandle,
        faireValues,
        additionalNotes
    ]);
    return {
        yearEstablished: yearEstablished,
        instagramHandle: instagramHandle,
        faireValues: faireValues,
        additionalNotes: additionalNotes,
        handleYearEstablishedChanged: handleYearEstablishedChanged,
        handleInstagramHandleChanged: handleInstagramHandleChanged,
        isFaireValueSelected: isFaireValueSelected,
        onChangeFaireValue: onChangeFaireValue,
        setAdditionalNotes: setAdditionalNotes,
        errors: errors,
        onSave: onSave,
        formSubmitted: formSubmitted,
        isPostingAdditionalInfo: isPostingAdditionalInfo
    };
};
